<template>
  <v-container>
    <back-button :label="transPlural('settlement.settlement')"></back-button>

    <request-state :request-promise="settlementPromise">
      <template v-if="settlement">
        <page-heading class="mb-0">
          {{ pageTitle }}
          <div class="text-subtitle-1 font-weight-thin pt-0">{{ trans('general.createdAt', {date: formatDate(settlement.createdAt)}) }}</div>
        </page-heading>

        <v-row class="pb-2">
          <v-col>
            <stat-number
              :label="trans('settlement.commissionNet')"
            >
              <currency-amount :amount="settlement.provisionNet"></currency-amount>
            </stat-number>
          </v-col>
          <v-col>
            <stat-number
              :label="trans('settlement.commissionGross')"
            >
              <currency-amount :amount="settlement.provisionGross"></currency-amount>
            </stat-number>
          </v-col>

          <v-col>
            <stat-number
              :label="trans('settlement.saldoTotal')"
            >
              <currency-amount :amount="settlement.saldoTotal"></currency-amount>
            </stat-number>
          </v-col>

          <v-col class="d">
            <stat-number
              :label="trans('settlement.status')"
            >
              <settlement-status :settlement="settlement"/>
            </stat-number>
          </v-col>
        </v-row>

        <div class="d-flex justify-end flex-wrap">
          <settlement-update-bookings-button
            class="ml-2 mt-2"
            @click="pushToRoute('SettlementUpdateBookings', {settlementId: settlementId})"
            v-if="numberOfOpenBookings === 0"
          ></settlement-update-bookings-button>

          <v-btn
            class="ml-2 mt-2"
            @click="downloadSettlementObjectPdfPackage"
            :loading="downloadingSettlementObjectPdfPackage"
          ><v-icon left>mdi-download</v-icon>{{ trans('settlement.downloadPdfPackage') }}
            <help-tooltip translation-key="settlement.downloadPdfPackageHelp"/>
          </v-btn>

          <v-btn
            class="ml-2 mt-2"
            @click="pushToRoute('SettlementSendToOwners', {settlementId: settlementId})"
          ><v-icon left>mdi-email-send</v-icon>{{ trans('settlement.sendToOwners') }}
            <help-tooltip translation-key="settlement.sendToOwnersActionHelp"/>
          </v-btn>

          <v-btn
            class="ml-2 mt-2"
            @click="downloadSettlementDatevCsvPackage"
            :loading="downloadingSettlementDatevCsvPackage"
          ><v-icon left>mdi-download</v-icon>{{ trans('settlement.downloadDatevCsvPackage') }}
            <help-tooltip translation-key="settlement.downloadDatevCsvPackageHelp"/>
          </v-btn>

          <v-menu offset-y>
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                class="ml-2 mt-2"
                :loading="downloadingSepa"
                v-bind="attrs"
                v-on="on"
              >
                <v-icon left>mdi-download</v-icon>{{ trans('settlement.downloadSepa') }}
                <help-tooltip translation-key="settlement.downloadSepaHelp"/>
                <v-icon right>mdi-chevron-down</v-icon>
              </v-btn>
            </template>
            <v-list dense>
              <v-list-item
                @click="downloadSettlementSepa('ct')"
              >
                <v-list-item-title>{{ trans('settlement.downloadSepaCt') }}</v-list-item-title>
              </v-list-item>
              <v-list-item
                @click="downloadSettlementSepa('dd')"
              >
                <v-list-item-title>{{ trans('settlement.downloadSepaDd') }}</v-list-item-title>
              </v-list-item>
            </v-list>
          </v-menu>

          <v-menu
            offset-y
          >
            <template v-slot:activator="{ on }">
              <v-btn class="ml-2 mt-2" v-on="on" :loading="renewLoading || deleteLoading">{{ trans('general.more') }}<v-icon right>mdi-chevron-down</v-icon></v-btn>
            </template>

            <v-list dense>
              <v-list-item @click="showRenewModal = true">
                <v-list-item-icon><v-icon left small>mdi-reload</v-icon></v-list-item-icon>
                <v-list-item-title>{{ trans('general.renew') }}</v-list-item-title>
              </v-list-item>
              <v-list-item @click="showDeleteModal = true">
                <v-list-item-icon><v-icon left small>mdi-delete</v-icon></v-list-item-icon>
                <v-list-item-title>{{ trans('general.delete') }}</v-list-item-title>
              </v-list-item>
            </v-list>
          </v-menu>
        </div>

        <v-slide-y-transition>
          <info-alert v-show="numberOfOpenBookings > 0" class="mt-5">
            <div>{{ transc('settlement.openBookingsAvailable', numberOfOpenBookings, {numberOfOpenBookings: numberOfOpenBookings}) }}</div>
            <settlement-update-bookings-button
              color="primary"
              class="mt-2"
              @click="pushToRoute('SettlementUpdateBookings', {settlementId: settlementId})"
              depressed
            ></settlement-update-bookings-button>
          </info-alert>
        </v-slide-y-transition>

        <v-slide-y-transition v-if="missingObjectsPage">
          <info-alert v-show="missingObjectsPage && missingObjectsPage.totalElements > 0" class="mt-5">
            <div>{{
                transc('settlement.missingObjectsForSettlement', missingObjectsPage.totalElements, {numberOfObjects: missingObjectsPage.totalElements})
              }}</div>
            <ul>
              <li v-for="object in missingObjectsPage.content" :key="object.id">{{ object.name }}</li>
              <li v-if="!missingObjectsPage.last">...</li>
            </ul>
            <v-btn
              color="primary"
              class="mt-2"
              @click="createMissingSettlementObjects"
              depressed
              :loading="missingObjectsLoading"
            ><v-icon left class="mr-3">mdi-note-plus-outline</v-icon>{{
                transc('settlement.createMissingSettlementObjects', missingObjectsPage.totalElements)
              }}
            </v-btn>
          </info-alert>
        </v-slide-y-transition>

        <v-tabs
          v-model="currentTab"
          class="mt-7"
        >
          <v-tab>{{ transPlural('settlement.settlement') }}</v-tab>
          <v-tab>{{ transPlural('booking.booking') }}</v-tab>
          <v-tab>{{ transPlural('misc.miscsObject') }}</v-tab>
        </v-tabs>
        <v-tabs-items
          v-model="currentTab"
        >
          <v-tab-item>
            <ajax-table
              ref="settlementObjects"
              :resource="baseResourceUrl + '/objects'"
              :headers="[
                {text: 'object.object', value: 'object.name'},
                {text: 'settlement.commissionNet', value: 'provisionNet', type: 'currency'},
                {text: 'settlement.commissionGross', value: 'provisionGross', type: 'currency'},
                {text: 'settlement.saldoTotal', value: 'saldo', type: 'currency'},
                {text: 'object.settlementNotes', value: 'settlementNotes'},
                {text: '', value: 'statusDetailed', sortable: false},
                {text: '', value: 'actions', sortable: false},
              ]"
              :page-options="{itemsPerPage: 20, sortBy: ['object.name']}"
              :items-per-page-options="[10, 20, 50]"
              :filter="settlementObjectsFilter"
            >
              <template v-slot:filters>
                <v-col cols="6" sm="6" md="4" lg="3">
                  <v-select
                      v-model="settlementObjectsFilterData.checked"
                      :label="trans('settlement.filterByChecked')"
                      :items="filterSettlementObjectsCheckedOptions"
                      single-line
                      hide-details
                      clearable
                      class="pt-0"
                  >
                    <template v-slot:selection="{item}">
                      <settlement-checked-icon :value="item.value" class="mr-2"/>
                      {{ item.text }}
                    </template>

                    <template v-slot:item="{item, on, attrs}">
                      <v-list-item v-bind="attrs" v-on="on">
                        <v-list-item-icon>
                          <settlement-checked-icon :value="item.value" />
                        </v-list-item-icon>
                        <v-list-item-content>
                          <v-list-item-title>
                            {{ item.text }}
                          </v-list-item-title>
                        </v-list-item-content>
                      </v-list-item>
                    </template>
                  </v-select>
                </v-col>

                <v-col cols="6" sm="6" md="4" lg="3">
                  <v-select
                      v-model="settlementObjectsFilterData.sent"
                      :label="trans('settlement.filterBySent')"
                      :items="filterSettlementObjectsSentOptions"
                      single-line
                      hide-details
                      clearable
                      class="pt-0"
                  >
                    <template v-slot:selection="{item}">
                      <settlement-owner-email-icon :value="item.value" class="mr-2" />
                      {{ item.text }}
                    </template>

                    <template v-slot:item="{item, on, attrs}">
                      <v-list-item v-bind="attrs" v-on="on">
                        <v-list-item-icon>
                          <settlement-owner-email-icon :value="item.value" class="ml-2" />
                        </v-list-item-icon>
                        <v-list-item-content>
                          <v-list-item-title>
                            {{ item.text }}
                          </v-list-item-title>
                        </v-list-item-content>
                      </v-list-item>
                    </template>
                  </v-select>
                </v-col>

                <v-col cols="6" sm="6" md="4" lg="3">
                  <v-select
                      v-model="settlementObjectsFilterData.datev"
                      :label="trans('settlement.filterByDatev')"
                      :items="filterSettlementObjectsDownloadedOptions"
                      single-line
                      hide-details
                      clearable
                      class="pt-0"
                  >
                    <template v-slot:selection="{item}">
                      <settlement-datev-downloaded-icon :value="item.value" class="mr-2" />
                      {{ item.text }}
                    </template>

                    <template v-slot:item="{item, on, attrs}">
                      <v-list-item v-bind="attrs" v-on="on">
                        <v-list-item-icon>
                          <settlement-datev-downloaded-icon :value="item.value" class="mr-2" />
                        </v-list-item-icon>
                        <v-list-item-content>
                          <v-list-item-title>
                            {{ item.text }}
                          </v-list-item-title>
                        </v-list-item-content>
                      </v-list-item>
                    </template>
                  </v-select>
                </v-col>

                <v-col cols="6" sm="6" md="4" lg="3">
                  <v-select
                      v-model="settlementObjectsFilterData.sepa"
                      :label="trans('settlement.filterBySepa')"
                      :items="filterSettlementObjectsDownloadedOptions"
                      single-line
                      hide-details
                      clearable
                      class="pt-0"
                  >
                    <template v-slot:selection="{item}">
                      <settlement-sepa-downloaded-icon :value="item.value" class="mr-2" />
                      {{ item.text }}
                    </template>

                    <template v-slot:item="{item, on, attrs}">
                      <v-list-item v-bind="attrs" v-on="on">
                        <v-list-item-icon>
                          <settlement-sepa-downloaded-icon :value="item.value" class="mr-2" />
                        </v-list-item-icon>
                        <v-list-item-content>
                          <v-list-item-title>
                            {{ item.text }}
                          </v-list-item-title>
                        </v-list-item-content>
                      </v-list-item>
                    </template>
                  </v-select>
                </v-col>

                <v-col cols="6" sm="6" md="4" lg="3" offset="0" offset-sm="0" offset-md="0" offset-lg="6">
                  <select-object hide-details :object-id.sync="settlementObjectsFilterData.objectId"/>
                </v-col>

                <v-col cols="6" sm="6" md="4" lg="3">
                  <search v-model="settlementObjectsFilterData.objectName" />
                </v-col>
              </template>

              <template v-slot:item.object.name="{item}">
                <object-name class="mt-2 mb-1" :value="item.object" />
                <div class="subtext mb-2">
                  <div>{{ item.object.owner.fullName }}</div>
                  <div>{{ item.object.street }} {{ item.object.houseNumber }}</div>
                  <div>{{ item.object.zipcode }} {{ item.object.city }}</div>
                  <div v-if="!isCountryGermany(item.object.country)">{{ item.object.country }}</div>
                </div>
              </template>

              <template v-slot:item.settlementNotes="{item}">
                <div class="subtext">{{ item.settlementNotes || '-' }}</div>
              </template>

              <template v-slot:item.statusDetailed="{item}">
                <div style="white-space: nowrap">
                  <settlement-checked-icon :value="item" />

                  <template v-if="item.sendableByAmount || item.lastOwnerEmail">
                    <settlement-owner-email-icon :value="item" class="ml-2" v-if="item.lastOwnerEmail" @click="getEmail(item.lastOwnerEmail)" />
                    <settlement-owner-email-icon :value="item" class="ml-2" v-else />
                  </template>

                  <settlement-datev-downloaded-icon :value="item" class="ml-2" v-if="item.provisionGross !== 0 || item.saldo !== 0" />

                  <settlement-sepa-downloaded-icon :value="item" class="ml-2" v-if="item.saldo !== 0" />
                </div>

              </template>

              <template v-slot:item.actions="{ item }">
                <context-menu-icon
                  :loading="isSettlementObjectRequestInProgress(item)"
                >
                  <v-list dense>
                    <v-list-item @click="toggleSettlementObjectChecked(item)">
                      <v-list-item-icon><v-icon left small>{{ item.checked ? 'mdi-shield-alert' : 'mdi-shield-check' }}</v-icon></v-list-item-icon>
                      <v-list-item-title>{{ trans(item.checked ? 'settlement.setNotChecked' : 'settlement.setChecked') }}</v-list-item-title>
                    </v-list-item>
                    <template v-if="item.sendableByAmount || item.lastOwnerEmail">
                      <v-list-item @click="sendSettlementObjectToOwner(item)" v-if="item.checked && !item.sent">
                        <v-list-item-icon><v-icon left small>mdi-email-send</v-icon></v-list-item-icon>
                        <v-list-item-title>{{ trans('settlement.sendToOwners') }}</v-list-item-title>
                      </v-list-item>
                      <v-list-item @click="toggleSettlementObjectSentToOwner(item)">
                        <v-list-item-icon><v-icon left small>{{ item.sent ? 'mdi-email-send' : 'mdi-email-check' }}</v-icon></v-list-item-icon>
                        <v-list-item-title>{{ trans(item.sent ? 'settlement.setNotSent' : 'settlement.setSent') }}</v-list-item-title>
                      </v-list-item>
                    </template>
                    <v-list-item v-if="item.lastOwnerEmail" @click="getEmail(item.lastOwnerEmail)">
                      <v-list-item-icon><v-icon left small>mdi-email-search</v-icon></v-list-item-icon>
                      <v-list-item-title>{{ trans('emailSettings.showEmail') }}</v-list-item-title>
                    </v-list-item>
                    <v-list-item @click="downloadSettlementObjectPdf(item)">
                      <v-list-item-icon><v-icon left small>mdi-file-download-outline</v-icon></v-list-item-icon>
                      <v-list-item-title>{{ trans('settlement.downloadPdf') }}</v-list-item-title>
                    </v-list-item>
                    <v-list-item @click="downloadSettlementObjectDatevCsv(item)" v-if="item.provisionGross !== 0 || item.saldo !== 0">
                      <v-list-item-icon><v-icon left small>mdi-download</v-icon></v-list-item-icon>
                      <v-list-item-title>{{ trans('settlement.downloadDatevCsv') }}</v-list-item-title>
                    </v-list-item>
                    <v-list-item @click="downloadSettlementObjectSepa(item)" v-if="item.saldo !== 0">
                      <v-list-item-icon><v-icon left small>mdi-download</v-icon></v-list-item-icon>
                      <v-list-item-title>{{ trans('settlement.downloadSepa') }}</v-list-item-title>
                    </v-list-item>
                    <v-list-item @click="objectToRenew = item">
                      <v-list-item-icon><v-icon left small>mdi-reload</v-icon></v-list-item-icon>
                      <v-list-item-title>{{ trans('settlement.settlementObjects.renew') }}</v-list-item-title>
                    </v-list-item>
                  </v-list>
                </context-menu-icon>
              </template>
            </ajax-table>

          </v-tab-item>
          <v-tab-item>
            <ajax-table
              ref="settlementBookings"
              :resource="baseResourceUrl + '/bookings'"
              :headers="[
                {text: 'booking.bookingId', value: 'id'},
                {text: 'booking.provisionSettlement', value: 'provisionSettlementId'},
                {text: 'booking.settleSettlement', value: 'settleSettlementId'},
                {text: 'object.object', value: 'objectName'},
                {text: 'guest.guest', value: 'guestName'},
                {text: 'booking.checkinAt', value: 'checkinAt'},
                {text: 'booking.checkoutAt', value: 'checkoutAt'},
                {text: 'booking.amountTotalWithoutDeposit', value: 'totalWithoutDepositAndBookingFee', type: 'currency'},
                {text: '', value: 'actions', sortable: false},
              ]"
              :page-options="{itemsPerPage: 20}"
              :items-per-page-options="[10, 20, 50]"
              @page-loaded="onBookingsLoaded"
              :filter="bookingsFilter"
            >
              <template v-slot:filters>
                <v-col cols="6" sm="6" md="6" lg="3">
                  <date-period-select v-model="bookingsFilterData.checkIn" :label="trans('booking.checkinAt')"/>
                </v-col>
                <v-col cols="6" sm="6" md="6" lg="3">
                  <date-period-select v-model="bookingsFilterData.checkOut" :label="trans('booking.checkoutAt')"/>
                </v-col>
                <v-col cols="6" sm="6" md="6" lg="3">
                  <select-object :object-id.sync="bookingsFilterData.objectId" clearable hide-details/>
                </v-col>
                <v-col cols="6" sm="6" md="6" lg="3">
                  <search v-model="bookingsFilterData.search" />
                </v-col>
              </template>

              <template v-slot:item.id="{ item }">
                <booking-id :value="item.id"/>
              </template>

              <template v-slot:item.provisionSettlementId="{ item }">
                <booking-settlement-status :booking="item" :settlement="settlement" type="provision" />
              </template>

              <template v-slot:item.settleSettlementId="{ item }">
                <booking-settlement-status :booking="item" :settlement="settlement" type="settle" />
              </template>

              <template v-slot:item.objectName="{item}">
                <object-name :value="item" />
              </template>

              <template v-slot:item.guestName="{item}">
                <guest-name :value="item" />
              </template>

            </ajax-table>
          </v-tab-item>
          <v-tab-item>
            <ajax-table
                ref="objectMiscsTable"
                :resource="baseResourceUrl + '/miscs'"
                :headers="[
                  {text: 'misc.settleDone', value: 'settleDone', width: 90},
                  {text: 'misc.object', value: 'object.name'},
                  {text: 'misc.description', value: 'description'},
                  {text: 'misc.account', value: 'account.accountNbr'},
                  {text: 'misc.receiptField', value: 'receiptField'},
                  {text: 'misc.price', value: 'price', type: 'currency'},
                  {text: 'misc.tax', value: 'tax', type: 'currency'},
                ]"
                :page-options="{itemsPerPage: 20}"
                :items-per-page-options="[10, 20, 50]"
                filterable
                :filter="miscFilter"
            >

              <template v-slot:filters>
                <select-object hide-details :object-id.sync="miscFilterData.objectId" style="max-width: 250px" clearable/>

                <v-checkbox hide-details :label="trans('misc.filter.settleDone')" v-model="miscFilterData.settleDone"></v-checkbox>
                <search v-model="miscFilterData.search"/>
              </template>

              <template v-slot:item.object.name="{item}">
                <object-name :value="item"/>
              </template>

              <template v-slot:item.settleDone="{item}">
                <status-badge
                    :color="item.settleDone ? 'success' : 'pending'"
                >{{ item.settleDone ? trans('misc.settleDoneTrue') : trans('misc.settleDoneFalse') }}
                </status-badge>
              </template>

              <template v-slot:item.account.accountNbr="{item}">
                <div v-if="item.account" class="mt-2 mb-1">{{ item.account.accountNbr }}</div>
              </template>

            </ajax-table>
          </v-tab-item>
        </v-tabs-items>
      </template>
    </request-state>

    <email-modal
      :show-modal.sync="showEmailModal"
      :email="currentEmail"
    ></email-modal>

    <validation-errors-modal
      :title="trans('general.couldNotDownloadSepa')"
      :show-modal.sync="showSepaValidationErrorsModal"
      :validation-errors.sync="sepaValidationErrors"
    >
    </validation-errors-modal>

    <validation-errors-modal
      :title="trans('settlement.settlementObjects.titleRenewError')"
      :show-modal.sync="showRenewErrorsModal"
      :validation-errors.sync="renewErrors"
    >
    </validation-errors-modal>

    <confirm-modal
      :show-modal.sync="showRenewModal"
      @confirm="renewSettlement"
      :title="trans('general.renew')"
      :confirm-button-label="trans('general.renew')"
      confirm-button-color="orange lighten-1"
    >
      {{ trans('settlement.confirmRenew') }}
    </confirm-modal>

    <confirm-modal
      :show-modal="objectToRenew != null"
      @close="objectToRenew = null"
      @confirm="renewSettlementObject(objectToRenew)"
      :title="trans('general.renew')"
      :confirm-button-label="trans('general.renew')"
      confirm-button-color="orange lighten-1"
    >
      {{ trans('settlement.confirmObjectRenew') }}
    </confirm-modal>

    <confirm-deletion-modal
      :show-modal.sync="showDeleteModal"
      @confirm="deleteSettlement"
    >
      {{ trans('settlement.confirmDelete') }}
    </confirm-deletion-modal>
  </v-container>
</template>

<script>
import AjaxTable from '../components/AjaxTable';
import {LocaleUtils} from '../utils/locale-utils';
import PageHeading from '../components/PageHeading';
import {DateUtils} from '../utils/date-utils';
import CurrencyAmount from '../components/CurrencyAmount';
import StatNumber from '../components/StatNumber';
import BackButton from '../components/BackButton';
import RequestState from '../components/RequestState';
import ContextMenuIcon from '../components/ContextMenuIcon';
import {RouterUtils} from '../utils/router-utils';
import {MainUtils} from '../utils/main-utils';
import {SettlementApi} from '../api/settlement-api';
import ConfirmDeletionModal from '../components/ConfirmDeletionModal';
import ConfirmModal from '../components/ConfirmModal';
import ValidationErrorsModal from '../components/ValidationErrorsModal';
import {SepaValidationErrorsUtils} from '../utils/sepa-validation-errors-utils';
import {comparison, eq} from 'rsql-builder';
import {like} from '@/lib/custom-rsql-operators';
import Search from '@/components/Search';
import ObjectName from '@/components/ObjectName';
import GuestName from '@/components/GuestName';
import BookingId from '@/components/BookingId';
import SettlementCheckedIcon from '@/components/icons/SettlementCheckedIcon';
import SettlementOwnerEmailIcon from '@/components/icons/SettlementOwnerEmailIcon';
import SelectObject from '../components/SelectObject';
import DatePeriodSelect from '../components/DatePeriodSelect';
import {FilterUtils} from '../utils/filter-utils';
import BookingSettlementStatus from '@/components/BookingSettlementStatus';
import SettlementDatevDownloadedIcon from '@/components/icons/SettlementDatevDownloadIcon';
import SettlementSepaDownloadedIcon from '@/components/icons/SettlementSepaDownloadIcon';
import SettlementStatus from '../components/SettlementStatus';
import StatusBadge from '@/components/StatusBadge';
import EmailModal from '../components/EmailModal';
import {EmailUtils} from '../utils/email-utils';
import InfoAlert from '@/components/InfoAlert';
import SettlementUpdateBookingsButton from '@/components/SettlementUpdateBookingsButton';
import HelpTooltip from '@/components/icons/HelpTooltip';
import dayjs from 'dayjs';

export default {
  name: 'SettlementDetailsPage',
  mixins: [
    MainUtils,
    RouterUtils,
    LocaleUtils,
    DateUtils,
    SepaValidationErrorsUtils,
    FilterUtils,
    EmailUtils
  ],
  components: {
    HelpTooltip,
    SettlementUpdateBookingsButton,
    InfoAlert,
    EmailModal,
    StatusBadge,
    SettlementStatus,
    SettlementSepaDownloadedIcon,
    SettlementDatevDownloadedIcon,
    BookingSettlementStatus,
    DatePeriodSelect,
    SelectObject,
    SettlementOwnerEmailIcon,
    SettlementCheckedIcon,
    ObjectName,
    GuestName,
    BookingId,
    ValidationErrorsModal,
    ConfirmModal,
    ConfirmDeletionModal,
    ContextMenuIcon,
    RequestState,
    BackButton,
    StatNumber,
    CurrencyAmount,
    PageHeading,
    AjaxTable,
    Search
  },
  props: {
    settlementId: {
      required: true
    }
  },
  data: () => ({
    /** @type {SettlementDto} */
    settlement: null,
    settlementPromise: null,
    numberOfBookings: null,
    numberOfOpenBookings: null,
    showRenewModal: false,
    objectToRenew: null,
    showDeleteModal: false,
    renewLoading: false,
    deleteLoading: false,
    missingObjectsLoading: false,
    downloadingSettlementObjectPdfPackage: false,
    downloadingSettlementDatevCsvPackage: false,
    downloadingSepa: false,
    /** @type {PageResponse<ObjectBasicDto>} */
    missingObjectsPage: null,
    renewErrors: {},
    showRenewErrorsModal: false,
    settlementObjectsFilterData: {
      checked: null,
      sent: null,
      datev: null,
      objectId: null,
      objectName: null
    },
    bookingsFilterData: {
      checkInFilter: null,
      checkOutFilter: null,
      objectId: null,
      search: null
    },
    miscFilterData: {
      objectId: null,
      settleDone: null,
      search: null
    },
    settlementObjectsRequestsInProgress: [],
    initLoaded: false,
    defaultStatusIconColor: '#BBB',
    currentTab: null
  }),

  computed: {
    filterSettlementObjectsCheckedOptions () {
      return [
        {value: false, text: this.trans('settlement.isNotChecked')},
        {value: true, text: this.trans('settlement.isChecked')},
      ];
    },
    filterSettlementObjectsSentOptions () {
      return [
        {value: false, text: this.trans('settlement.isNotSent')},
        {value: true, text: this.trans('settlement.isSent')},
      ];
    },
    filterSettlementObjectsDownloadedOptions () {
      return [
        {value: false, text: this.trans('settlement.isNotDownloaded')},
        {value: true, text: this.trans('settlement.isDownloaded')},
      ];
    },
    settlementObjectsFilter () {
      let filters =  [];
      if(this.settlementObjectsFilterData.checked != null) {
        filters.push(comparison('checked', eq(this.settlementObjectsFilterData.checked)))
      }
      if(this.settlementObjectsFilterData.sent != null) {
        filters.push(comparison('sent', eq(this.settlementObjectsFilterData.sent)))
      }
      if(this.settlementObjectsFilterData.datev != null) {
        filters.push(comparison('isDatevDownloaded', eq(this.settlementObjectsFilterData.datev)))
      }
      if(this.settlementObjectsFilterData.sepa != null) {
        filters.push(comparison('isSepaDownloaded', eq(this.settlementObjectsFilterData.sepa)))
      }
      if(this.settlementObjectsFilterData.objectId) {
        filters.push(comparison('object.id', eq(this.settlementObjectsFilterData.objectId)))
      }
      if(this.settlementObjectsFilterData.objectName) {
        filters.push(comparison('object.name', like(this.settlementObjectsFilterData.objectName)))
      }
      return filters;
    },
    bookingsFilter () {
      let filters =  [];

      if(this.bookingsFilterData.objectId != null) {
        filters.push(comparison('object.id', eq(this.bookingsFilterData.objectId)))
      }
      if(this.bookingsFilterData.search) {
        filters.push(this.buildSearchFilter(this.bookingsFilterData.search, ['object.name', 'guest.firstName', 'guest.lastName', 'id']))
      }
      if(this.bookingsFilterData.checkIn != null) {
        filters.push(this.buildDateFilter('checkinAt', this.bookingsFilterData.checkIn));
      }
      if(this.bookingsFilterData.checkOut != null) {
        filters.push(this.buildDateFilter('checkoutAt', this.bookingsFilterData.checkOut));
      }
      return filters;
    },
    miscFilter () {
      let filters =  [];

      if(this.miscFilterData.objectId != null) {
        filters.push(comparison('object.id', eq(this.miscFilterData.objectId)))
      }
      if(this.miscFilterData.settleDone) {
        filters.push(comparison('settleDone', eq(0)))
      }
      if(this.miscFilterData.search) {
        filters.push(this.buildSearchFilter(this.miscFilterData.search, ['description', 'receiptField']))
      }
      return filters;
    },
    baseResourceUrl () {
      return SettlementApi.apiUri + '/' + this.settlement.id;
    },
    pageTitle () {
      if (this.settlement) {
        return this.getMonthNameByIndex(this.settlement.month) + ' ' +  this.settlement.year + ' - ' + this.transSingular('settlement.settlement', 1);
      }
      return null;
    }
  },

  mounted () {
    this.getSettlement();
  },

  activated () {
    this.getSettlement();
    if (this.$refs.settlementObjects) {
      this.$refs.settlementObjects.loadData();
    }
    if (this.$refs.settlementBookings) {
      this.$refs.settlementBookings.loadData();
    }
    if (this.$refs.objectMiscsTable) {
      this.$refs.objectMiscsTable.loadData();
    }
  },

  watch: {
    settlementId: {
      handler: function(newId, oldId) {
        // reload if router changes only props (settlementId) because of reusing page with different params
        this.getSettlement();
      }
    }
  },

  methods: {
    getSettlement () {
      let settlementPromise = SettlementApi.getById(this.settlementId)
      if (!this.initLoaded) {
        this.settlementPromise = settlementPromise;
      }
      settlementPromise.then(response => {
        this.settlement = response.data;
        this.initLoaded = true;

        SettlementApi.validateSettlementBookings(this.settlementId)
        .then(response => {
          this.numberOfOpenBookings = response.data.numberOfOpenBookings;
        });

        if(dayjs(this.settlement.createdAt).isAfter(dayjs().subtract(3+1, 'month'))) {
          SettlementApi.getMissingSettlementObjects(this.settlementId, {params: {pageSize: 5}})
          .then(response => {
            this.missingObjectsPage = response.data;
          })
        }
      });
    },
    reloadData () {
      this.getSettlement();
      this.$refs.settlementObjects.loadData();
    },
    isCountryGermany (country) {
      return country === 'Deutschland';
    },
    onBookingsLoaded (response) {
      if (Object.keys(this.bookingsFilter).length === 0) {
        this.numberOfBookings = response.totalElements;
      }
    },
    /** @param settlementObject {SettlementObjectDto} */
    sendSettlementObjectToOwner (settlementObject) {
      this.pushToRoute('SettlementSendToOwners', {settlementId: this.settlementId, objects: [settlementObject.object]})
    },
    /** @param settlementObject {SettlementObjectDto} */
    toggleSettlementObjectSentToOwner (settlementObject) {
      this.setSettlementObjectRequestInProgress(settlementObject);
      SettlementApi.setSettlementObjectSentToOwner(this.settlement.id, settlementObject.id, !settlementObject.sent)
      .then(response => this.reloadData())
      .catch(this.handleErrorResponse)
      .finally(() => this.removeSettlementObjectRequestInProgress(settlementObject));
    },
    /** @param settlementObject {SettlementObjectDto} */
    toggleSettlementObjectChecked (settlementObject) {
      this.setSettlementObjectRequestInProgress(settlementObject);
      SettlementApi.setSettlementObjectChecked(this.settlement.id, settlementObject.id, !settlementObject.checked)
      .then(response => this.reloadData())
      .catch(this.handleErrorResponse)
      .finally(() => this.removeSettlementObjectRequestInProgress(settlementObject));
    },
    /** @param settlementObject {SettlementObjectDto} */
    downloadSettlementObjectPdf (settlementObject) {
      SettlementApi.downloadSettlementObjectPdf(this.settlementId, settlementObject.id)
      .then(this.downloadResponseAsFile)
      .catch(this.handleErrorResponse)
    },
    downloadSettlementObjectPdfPackage () {
      this.downloadingSettlementObjectPdfPackage = true;
      SettlementApi.downloadSettlementObjectPdfPackage(this.settlementId)
      .then(this.downloadResponseAsFile)
      .catch(this.handleErrorResponse)
      .finally(() => {
        this.downloadingSettlementObjectPdfPackage = false;
      })
    },
    /** @param settlementObject {SettlementObjectDto} */
    downloadSettlementObjectDatevCsv (settlementObject) {
      SettlementApi.downloadSettlementObjectDatevCsv(this.settlementId, settlementObject.id)
      .then(this.downloadResponseAsFile)
      .then(this.reloadData)
      .catch((response) => {
        let errorMessage = this.getErrorMessageByResponse(response);
        this.showErrorNotification(this.trans('settlement.couldNotDownloadDatevCsv') + ': ' + errorMessage)
      })
    },
    /** @param settlementObject {SettlementObjectDto} */
    downloadSettlementObjectSepa (settlementObject) {
      SettlementApi.downloadSettlementObjectSepa(this.settlementId, settlementObject.id)
      .then(this.downloadResponseAsFile)
      .then(this.reloadData)
      .catch(this.handleSepaValidationErrorResponse)
    },
    downloadSettlementSepa (type) {
      this.sepaValidationErrors = {};
      this.downloadingSepa = true;
      SettlementApi.downloadSettlementSepa(this.settlementId, encodeURIComponent(type.toLowerCase()))
      .then(this.downloadResponseAsFile)
      .then(this.reloadData)
      .catch(this.handleSepaValidationErrorResponse)
      .finally(() => {
        this.downloadingSepa = false;
      })
    },
    downloadSettlementDatevCsvPackage () {
      this.downloadingSettlementDatevCsvPackage = true;
      SettlementApi.downloadSettlementDatevCsvPackage(this.settlementId)
      .then(this.downloadResponseAsFile)
      .then(this.reloadData)
      .catch(this.handleErrorResponse)
      .finally(() => {
        this.downloadingSettlementDatevCsvPackage = false;
      })
    },
    deleteSettlement () {
      this.deleteLoading = true;
      SettlementApi.delete(this.settlementId)
        .then(response => {
          this.replaceToRoute('Settlements');
          this.showSavedSuccessfullyNotification();
        })
        .catch(this.handleErrorResponse)
        .finally(() => {
          this.deleteLoading = false;
        })
    },
    renewSettlement () {
      this.renewLoading = true;
      SettlementApi.updateSettlementBookings(this.settlementId, {})
        .then(response => {
          this.showSavedSuccessfullyNotification();
          const errors = response.data.objectExceptions;
          if(Object.keys(errors).length > 0) {
            this.showRenewErrors(errors)
          }
        })
        .catch(this.handleErrorResponse)
        .finally(() => {
          this.renewLoading = false;
          this.reloadData();
        })
    },
    /** @param settlementObject {SettlementObjectDto} */
    renewSettlementObject(settlementObject) {
      this.setSettlementObjectRequestInProgress(settlementObject);
      SettlementApi.renewSettlementObject(this.settlement.id, settlementObject.id)
      .catch(reason => this.showRenewErrors(this.getValidationErrors(reason), false))
      .finally(this.reloadData)
      .finally(() => this.removeSettlementObjectRequestInProgress(settlementObject));
    },
    showRenewErrors(errors) {
      this.renewErrors = errors;
      this.showRenewErrorsModal = errors != null;
    },
    createMissingSettlementObjects() {
      this.missingObjectsLoading = true;
      SettlementApi.createMissingSettlementObjects(this.settlement.id)
      .then(response => {
        const errors = response.data.objectExceptions;
        if(errors != null && Object.keys(errors).length > 0) {
          this.showRenewErrors(errors)
        }
      })
      .catch(this.handleErrorResponse)
      .finally(() => {
        this.missingObjectsLoading = false;
        this.reloadData();
      })
    },
    /** @param settlementObject {SettlementObjectDto} */
    setSettlementObjectRequestInProgress (settlementObject) {
      this.settlementObjectsRequestsInProgress.push(settlementObject);
    },
    /** @param settlementObject {SettlementObjectDto} */
    removeSettlementObjectRequestInProgress (settlementObject) {
      this.settlementObjectsRequestsInProgress = this.settlementObjectsRequestsInProgress
        .filter(settlementObject2 => settlementObject2 !== settlementObject);
    },
    /** @param settlementObject {SettlementObjectDto} */
    isSettlementObjectRequestInProgress (settlementObject) {
      return this.settlementObjectsRequestsInProgress.includes(settlementObject);
    }
  }
}
</script>

